import { z } from 'zod';
import { isoTimestampSchema, outcomeSchema } from './common.js';
/**
 * Everything the platform captures while a step runs. Evidence is what turns a
 * red badge into something a non-engineer can act on, so each kind carries a
 * human-readable caption alongside its payload.
 */
export const evidenceKindSchema = z.enum([
    'screenshot',
    'json',
    'text',
    'html',
    'audio',
    'har',
    'video',
    'link',
]);
export const evidenceSchema = z.object({
    id: z.string(),
    kind: evidenceKindSchema,
    caption: z.string(),
    /** Path relative to the run's artifact directory, for file-backed evidence. */
    path: z.string().optional(),
    /** Inline payload, for small JSON or text evidence that is not worth a file. */
    inline: z.unknown().optional(),
    /** External URL, for `link` evidence such as a Salesforce record. */
    url: z.string().optional(),
    capturedAt: isoTimestampSchema,
});
/**
 * A single verifiable claim. Steps produce many of these; the step's own outcome
 * is the roll-up of its assertions.
 *
 * `impact` is deliberately plain English: it is shown first when something fails
 * so that someone outside engineering understands what is broken for the user.
 */
export const assertionSchema = z.object({
    id: z.string(),
    title: z.string(),
    outcome: outcomeSchema,
    expected: z.string().optional(),
    actual: z.string().optional(),
    impact: z.string().optional(),
    /** Milliseconds the check itself took, where meaningful. */
    durationMs: z.number().int().nonnegative().optional(),
    evidence: z.array(evidenceSchema).default([]),
});
export const stepGroupSchema = z.enum([
    'preflight',
    'api-health',
    'install',
    'config',
    'lwc',
    'call',
    'postcall',
    'cleanup',
]);
export const stepResultSchema = z.object({
    stepId: z.string(),
    outcome: outcomeSchema,
    startedAt: isoTimestampSchema,
    endedAt: isoTimestampSchema.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    assertions: z.array(assertionSchema).default([]),
    evidence: z.array(evidenceSchema).default([]),
    /** Short plain-English summary rendered in the timeline. */
    summary: z.string().optional(),
    /** Operator-facing failure reason, shown above any stack trace. */
    reason: z.string().optional(),
    /** Raw error detail, hidden behind a disclosure in the UI. */
    errorDetail: z.string().optional(),
});
//# sourceMappingURL=evidence.js.map